import { Form, Formik, FormikProps } from 'formik'
import { Property, useCreateInterestCaseMutation } from 'generated/graphql'
import React, { useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Text,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import * as Yup from 'yup'
import { MotionFlex } from 'components'
import { ConnectedFormGroup } from 'components/FormElements'

import { formatError } from 'utils'
import { useAuthContext } from 'context/AuthProvider'
import { ERROR_TOAST, PHONE_NUMBER_REGEX, SUCCESS_TOAST } from '../../../../constants'
import ConnectedTextarea from 'components/FormElements/ConnectedTextArea'
import { ConfimationModal } from 'components/Property'
import { useHistory } from 'react-router'

import { PrimaryButton } from 'components/Buttons'
import { useTranslation } from 'react-i18next'

type ContactAgentFormValues = {
  username: string
  email: string
  cellNumber: string
  message: string
}
const ContactAgentFormValidation = Yup.object().shape({
  username: Yup.string().required('A username is required'),
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('An email address is required'),
  cellNumber: Yup.string()
    .matches(PHONE_NUMBER_REGEX, 'Please enter a valid cell number')
    .required('A cell number address is required'),
  message: Yup.string().required('A message address is required')
})

type ContactAgentFormProps = {
  property: Property
  onClose?: () => void
}

const ContactAgentForm = React.forwardRef<HTMLFormElement, ContactAgentFormProps>(
  ({ property, onClose }, ref) => {
    const { user, isAuthenticated } = useAuthContext()
    const toast = useToast()
    const [showConfimationMessage, setShowConfimationMessage] = useState(false)
    const {
      isOpen: confirmationModalIsOpen,
      onClose: confirmationModalOnClose,
      onOpen: confirmationModalOnOpen
    } = useDisclosure()
    const [createInterestCase] = useCreateInterestCaseMutation({
      onCompleted: () => {
        toast({
          ...SUCCESS_TOAST,
          title: `Thank you, ${property.agent?.name} will be in touch soon.`
        })
        setShowConfimationMessage(true)
        confirmationModalOnOpen()
        if (onClose) onClose()
      },
      onError: () => {
        toast({
          ...ERROR_TOAST,
          title: 'Something went wrong, try again.'
        })
      }
    })
    const { t } = useTranslation()
    const history = useHistory()
    return (
      <>
        <ConfimationModal
          property={property}
          isOpen={confirmationModalIsOpen}
          onClose={confirmationModalOnClose}
        />
        <Formik
          validationSchema={ContactAgentFormValidation}
          initialValues={{
            cellNumber: user?.phoneNumber ? user.phoneNumber : '',
            username: user?.username ? user.username : '',
            message: t('property_details.default_interest_message'),
            email: user?.email ? user.email : ''
          }}
          onSubmit={async (
            { cellNumber, username, message, email },
            { setStatus, setSubmitting }
          ) => {
            setStatus(null)
            try {
              setSubmitting(true)
              try {
                await createInterestCase({
                  variables: {
                    input: {
                      data: {
                        cell_number: cellNumber,
                        full_name: username,
                        message,
                        email,
                        property: property.id,
                        users_permissions_user: user?.id ? user.id : null
                      }
                    }
                  }
                })

                if (!isAuthenticated) history.push('/register', { cellNumber, username, email })
              } catch (e) {
                console.log(e)
              }
              setSubmitting(false)
            } catch (error: any) {
              setStatus(formatError(error))
            }
          }}
        >
          {({ isSubmitting, status }: FormikProps<ContactAgentFormValues>) => (
            <Form ref={ref} style={{ width: '100%' }} autoComplete="off">
              <ConnectedFormGroup
                name="username"
                isDisabled={isAuthenticated}
                label={
                  isAuthenticated
                    ? t('property_details.username_label')
                    : t('property_details.full_name_label')
                }
                placeholder={isAuthenticated ? 'Username' : 'Full Name'}
              />
              <ConnectedFormGroup
                name="email"
                isDisabled={isAuthenticated}
                label={t('property_details.email_label')}
                placeholder="Email"
                type="text"
              />
              <ConnectedFormGroup
                name="cellNumber"
                maxLength={10}
                label={t('property_details.phone_number_label')}
                placeholder="Cell Number"
              />
              <ConnectedTextarea
                name="message"
                fontSize="sm"
                label={t('property_details.message_label')}
                placeholder="Message"
                resize="none"
              />
              {status && (
                <MotionFlex initial={{ opacity: 0 }} animate={{ opacity: 1 }} mb={2} width="100%">
                  <Text textAlign="right" color="red.500">
                    {status}
                  </Text>
                </MotionFlex>
              )}
              {status && (
                <MotionFlex
                  animate={{ opacity: 1 }}
                  initial={{ opacity: 0 }}
                  justify="center"
                  my={4}
                  width="100%"
                >
                  <Alert variant="left-accent" status="error">
                    <AlertIcon />
                    <AlertTitle mr={2}>Error:</AlertTitle>
                    <AlertDescription>{status}</AlertDescription>
                  </Alert>
                </MotionFlex>
              )}
              <PrimaryButton
                my={6}
                width="100%"
                colorScheme="orange"
                size="md"
                rounded="md"
                type="submit"
                isLoading={isSubmitting}
                fontSize="xs"
              >
                {t('property_details.send_message')}
              </PrimaryButton>
            </Form>
          )}
        </Formik>
        {showConfimationMessage && (
          <Alert
            status="success"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="md">
              {t('property_details.application_submitted')}
            </AlertTitle>
            <AlertDescription maxWidth="xs">
              {t('property_details.application_submitted_description')}
            </AlertDescription>
          </Alert>
        )}
      </>
    )
  }
)

export default ContactAgentForm
